import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function NJLeadInspectionsPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchType, setSearchType] = useState<'municipality' | 'county'>('municipality');
  const [municipalitySearch, setMunicipalitySearch] = useState('');
  const [selectedCounty, setSelectedCounty] = useState('');
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Sample data - municipalities requiring dust wipe sampling
  const dustWipeMunicipalities = [
    { name: 'Newark', county: 'Essex' },
    { name: 'Jersey City', county: 'Hudson' },
    { name: 'Paterson', county: 'Passaic' },
    { name: 'Elizabeth', county: 'Union' },
    { name: 'Trenton', county: 'Mercer' },
    { name: 'Camden', county: 'Camden' },
    { name: 'Passaic', county: 'Passaic' },
    { name: 'Union City', county: 'Hudson' },
    { name: 'Bayonne', county: 'Hudson' },
    { name: 'East Orange', county: 'Essex' },
    { name: 'New Brunswick', county: 'Middlesex' },
    { name: 'Perth Amboy', county: 'Middlesex' },
    { name: 'Hoboken', county: 'Hudson' },
    { name: 'Plainfield', county: 'Union' },
    { name: 'West New York', county: 'Hudson' },
  ];

  const counties = [
    'Atlantic', 'Bergen', 'Burlington', 'Camden', 'Cape May', 'Cumberland',
    'Essex', 'Gloucester', 'Hudson', 'Hunterdon', 'Mercer', 'Middlesex',
    'Monmouth', 'Morris', 'Ocean', 'Passaic', 'Salem', 'Somerset', 'Sussex',
    'Union', 'Warren'
  ];

  const filteredMunicipalities = municipalitySearch
    ? dustWipeMunicipalities.filter(m =>
        m.name.toLowerCase().includes(municipalitySearch.toLowerCase())
      )
    : [];

  const municipalitiesByCounty = selectedCounty
    ? dustWipeMunicipalities.filter(m => m.county === selectedCounty)
    : [];

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/contact#contact-form');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              New Jersey Lead Inspections
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              New Jersey has some of the strictest lead-based paint regulations in the country. Whether you're a landlord, property manager, or homeowner, LCA Environmental provides the inspections, certifications, and compliance support you need to meet NJ's lead-safe housing requirements.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-[#f59e0b] text-white px-10 py-4 rounded-lg text-base font-semibold hover:bg-[#d97706] transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Request NJ Lead Inspection
            </Link>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Content Card */}
          <div className="bg-gray-50 rounded-3xl p-8 md:p-12 shadow-xl border border-gray-200 mb-16">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  New Jersey's newest lead-based paint law, <strong>"New Jersey's Lead-Safe Certification,"</strong> went into effect{' '}
                  <strong>July 22, 2022</strong> and directly targets <strong>all pre-1978 rental properties</strong>.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  In addition to new lead paint inspection intervals, New Jersey also requires{' '}
                  <strong>different testing methods depending on where your rental unit is located</strong>.
                </p>

                {/* Municipality Requirements Box */}
                <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl p-6 border border-gray-300 mb-8">
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-black mb-4">
                      <i className="ri-map-pin-line text-[#f59e0b] mr-2"></i>
                      Municipality-Specific Requirements
                    </h3>
                    <p className="text-gray-700 mb-4">
                      New Jersey requires different testing methods depending on your municipality. Most areas require{' '}
                      <strong>Visual Inspection</strong>, but some municipalities require <strong>Dust Wipe Sampling</strong>.
                    </p>

                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <h4 className="font-semibold text-black mb-2">Visual Inspection</h4>
                        <p className="text-sm text-gray-600 mb-2">Required in most municipalities</p>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Standard Cost</span>
                      </div>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <h4 className="font-semibold text-black mb-2">Dust Wipe Sampling</h4>
                        <p className="text-sm text-gray-600 mb-2">Required in 51 municipalities</p>
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">Premium Cost</span>
                      </div>
                    </div>

                    {/* Search Tool */}
                    <div className="bg-white rounded-lg p-6 border border-gray-200">
                      <h4 className="font-semibold text-black mb-6">Check Your Municipality's Requirements</h4>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-6">
                        <button
                          onClick={() => setSearchType('municipality')}
                          className={`p-4 border-2 rounded-lg text-left transition-all cursor-pointer ${
                            searchType === 'municipality'
                              ? 'border-[#f59e0b] bg-orange-50'
                              : 'border-gray-200 hover:border-[#f59e0b] hover:bg-orange-50'
                          }`}
                        >
                          <div className="flex items-center mb-2">
                            <i className="ri-search-line text-[#f59e0b] text-xl mr-3"></i>
                            <span className="font-semibold text-black">Search by Municipality</span>
                          </div>
                          <p className="text-sm text-gray-600">Type your municipality name for search results</p>
                        </button>
                        
                        <button
                          onClick={() => setSearchType('county')}
                          className={`p-4 border-2 rounded-lg text-left transition-all cursor-pointer ${
                            searchType === 'county'
                              ? 'border-[#f59e0b] bg-orange-50'
                              : 'border-gray-200 hover:border-[#f59e0b] hover:bg-orange-50'
                          }`}
                        >
                          <div className="flex items-center mb-2">
                            <i className="ri-map-pin-line text-[#f59e0b] text-xl mr-3"></i>
                            <span className="font-semibold text-black">Search by County</span>
                          </div>
                          <p className="text-sm text-gray-600">Select your county to see all municipalities</p>
                        </button>
                      </div>

                      {searchType === 'municipality' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Search for your municipality:
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={municipalitySearch}
                              onChange={(e) => {
                                setMunicipalitySearch(e.target.value);
                                setShowResults(e.target.value.length > 0);
                              }}
                              placeholder="Type your municipality name..."
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f59e0b] focus:border-[#f59e0b]"
                            />
                            <i className="ri-search-line absolute right-3 top-3.5 text-gray-400"></i>
                          </div>
                          {showResults && municipalitySearch && (
                            <div className="mt-2 space-y-1">
                              {filteredMunicipalities.length > 0 ? (
                                filteredMunicipalities.map((m) => (
                                  <div key={m.name} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                                    <p className="font-semibold text-black">{m.name}, {m.county} County</p>
                                    <p className="text-sm text-gray-600">
                                      <strong>Requires:</strong> Dust Wipe Sampling
                                    </p>
                                  </div>
                                ))
                              ) : (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                  <p className="text-sm text-gray-700">
                                    <strong>Good news!</strong> Your municipality likely requires standard Visual Inspection only.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {searchType === 'county' && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select your county:
                          </label>
                          <select
                            value={selectedCounty}
                            onChange={(e) => {
                              setSelectedCounty(e.target.value);
                              setShowResults(true);
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f59e0b] focus:border-[#f59e0b]"
                          >
                            <option value="">Select a county...</option>
                            {counties.map((county) => (
                              <option key={county} value={county}>{county}</option>
                            ))}
                          </select>
                          {showResults && selectedCounty && (
                            <div className="mt-4 border-t pt-4">
                              <h5 className="font-semibold text-black mb-4">
                                Municipalities in {selectedCounty} County requiring Dust Wipe Sampling:
                              </h5>
                              {municipalitiesByCounty.length > 0 ? (
                                <div className="space-y-2">
                                  {municipalitiesByCounty.map((m) => (
                                    <div key={m.name} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                                      <p className="font-semibold text-black">{m.name}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                  <p className="text-sm text-gray-700">
                                    No municipalities in {selectedCounty} County require Dust Wipe Sampling. Standard Visual Inspection applies.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-lg text-gray-700 leading-relaxed">
                  Landlords must provide evidence of a lead-safe certification to tenants and must affix a copy of that certification as an exhibit to the tenant's lease.
                </p>
              </div>

              {/* Three Key Points */}
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border border-orange-200 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#f59e0b] to-[#d97706] rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-calendar-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-black mb-4">July 22, 2022 Effective Date</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    The new Lead-Safe Certification law went into effect on this date, targeting all pre-1978 rental properties.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      Targets pre-1978 properties
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      New inspection intervals
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      Location-specific testing
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-black to-gray-800 rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-shield-check-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-black mb-4">Certification Requirements</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Landlords must provide evidence of lead-safe certification to tenants and include it in lease agreements.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      Evidence to tenants required
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      Must be in lease agreement
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      Official certification needed
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border border-orange-200 hover:shadow-xl transition-all duration-300">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#f59e0b] to-[#d97706] rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-map-pin-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-black mb-4">Location-Specific Testing</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Different testing methods are required depending on where your rental unit is located in New Jersey.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      Varies by municipality
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      Different methods required
                    </li>
                    <li className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-[#f59e0b] mr-2"></i>
                      Check local requirements
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Understanding Requirements Section */}
          <div className="mb-16">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-black mb-6">Understanding Lead-Safe Requirements</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Learn about exemptions, certifications, and testing methods for New Jersey's Lead-Safe Law
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Exemptions */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <h3 className="text-2xl font-bold text-black mb-4">
                    <i className="ri-shield-check-line text-green-600 mr-3"></i>
                    Exemptions
                  </h3>
                  <p className="text-gray-600 mb-6">
                    If your property qualifies for any of the following, you are <strong>exempt</strong> from the new inspection requirements:
                  </p>
                  <ol className="space-y-4 text-gray-600">
                    <li className="flex items-start">
                      <span className="bg-green-100 text-green-800 text-sm font-semibold px-2 py-1 rounded-full mr-3 mt-1 min-w-[24px] text-center">1</span>
                      <span>Constructed after 1978</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-green-100 text-green-800 text-sm font-semibold px-2 py-1 rounded-full mr-3 mt-1 min-w-[24px] text-center">2</span>
                      <span>Certified to be free of lead-based paint</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-green-100 text-green-800 text-sm font-semibold px-2 py-1 rounded-full mr-3 mt-1 min-w-[24px] text-center">3</span>
                      <span>Located in a multiple dwelling registered with the DCA for at least 10 years with <strong>no open violations</strong> in the past two years</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-green-100 text-green-800 text-sm font-semibold px-2 py-1 rounded-full mr-3 mt-1 min-w-[24px] text-center">4</span>
                      <span>Seasonal units rented for less than six months each year without consecutive renewals (e.g., short-term shore rentals)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-green-100 text-green-800 text-sm font-semibold px-2 py-1 rounded-full mr-3 mt-1 min-w-[24px] text-center">5</span>
                      <span>Owner-occupied units</span>
                    </li>
                  </ol>
                </div>

                {/* Lead-Free Certification */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <h3 className="text-2xl font-bold text-black mb-4">
                    <i className="ri-file-text-line text-[#f59e0b] mr-3"></i>
                    Lead-Free Certification Explained
                  </h3>
                  <p className="text-gray-600 mb-4">
                    A <strong>Lead-Free Certification</strong> means there is no lead paint in the rental unit or interior common areas.
                  </p>
                  <p className="text-gray-600 mb-4">
                    This certification can only be achieved through a <strong>lead-based paint inspection using an XRF instrument</strong>.
                  </p>
                  <p className="text-gray-600">
                    If, during a visual inspection, chipping paint is noticed, an <strong>XRF measurement</strong> can confirm whether the surface is a true lead-based paint hazard.
                  </p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Why Choose XRF */}
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                  <h3 className="text-2xl font-bold text-black mb-4">
                    <i className="ri-flask-line text-[#f59e0b] mr-3"></i>
                    Why Choose XRF Testing?
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Many landlords ask: <em>"Why spend more money on XRF testing if I don't have to?"</em>
                  </p>
                  <p className="text-gray-600 mb-6">The answer is simple:</p>
                  <ul className="space-y-4 text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#f59e0b] mr-3 mt-1 text-xl flex-shrink-0"></i>
                      <div>
                        <strong>Lower cost long term:</strong> XRF testing adds a small upfront cost but can save thousands in abatement or reinspection fees.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#f59e0b] mr-3 mt-1 text-xl flex-shrink-0"></i>
                      <div>
                        <strong>Definitive results:</strong> It's the only way to confirm that a surface does <strong>not</strong> contain lead-based paint.
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#f59e0b] mr-3 mt-1 text-xl flex-shrink-0"></i>
                      <div>
                        <strong>Permanent exemption:</strong> If no lead is found, your dwelling receives a <strong>Lead-Free Certification</strong>—which permanently exempts it from future inspection requirements.
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Key Benefits */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border border-orange-200">
                  <h3 className="text-2xl font-bold text-black mb-6">
                    <i className="ri-trophy-line text-[#f59e0b] mr-3"></i>
                    Key Benefits of XRF Testing
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                      <h4 className="font-semibold text-black mb-2">Cost Savings</h4>
                      <p className="text-sm text-gray-600">Avoid expensive abatement costs and repeated inspections</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                      <h4 className="font-semibold text-black mb-2">Definitive Results</h4>
                      <p className="text-sm text-gray-600">Get clear, scientific confirmation of lead presence or absence</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-orange-200">
                      <h4 className="font-semibold text-black mb-2">Permanent Solution</h4>
                      <p className="text-sm text-gray-600">Lead-Free Certification provides permanent exemption from future inspections</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Official Resources Section */}
          <div className="mb-16">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-black mb-6">Official Resources &amp; References</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Access official government resources and documentation for New Jersey's Lead-Safe Law
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Government Resources */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
                  <h3 className="text-2xl font-bold text-black mb-6">
                    <i className="ri-government-line text-[#f59e0b] mr-3"></i>
                    Government Resources
                  </h3>
                  <div className="space-y-4">
                    <a
                      href="https://www.nj.gov/health/childhood-lead/prevention/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-gray-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">NJ Department of Health – Childhood Lead Prevention</h4>
                          <p className="text-sm text-gray-600">Official New Jersey Department of Health Childhood Lead Prevention information</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                    <a
                      href="https://www.epa.gov/lead"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-gray-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">EPA Lead Information</h4>
                          <p className="text-sm text-gray-600">Environmental Protection Agency lead safety resources</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                    <a
                      href="https://www.cdc.gov/nceh/lead/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-gray-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">CDC Lead Prevention</h4>
                          <p className="text-sm text-gray-600">Centers for Disease Control lead prevention guidelines</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                    <a
                      href="https://www.hud.gov/program_offices/healthy_homes/enforcement/lbp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-gray-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">HUD Lead-Based Paint</h4>
                          <p className="text-sm text-gray-600">Department of Housing and Urban Development lead-based paint enforcement</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Legal Documentation */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
                  <h3 className="text-2xl font-bold text-black mb-6">
                    <i className="ri-file-text-line text-[#f59e0b] mr-3"></i>
                    Legal Documentation
                  </h3>
                  <div className="space-y-4">
                    <a
                      href="https://www.njleg.state.nj.us/bills/BillView.asp"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-gray-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">Lead-Safe Law Text</h4>
                          <p className="text-sm text-gray-600">Full text of New Jersey's Lead-Safe Law legislation</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                    <a
                      href="https://www.nj.gov/dca/divisions/codes/lead/forms.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-gray-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">Official Forms</h4>
                          <p className="text-sm text-gray-600">Required forms and documentation for compliance</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Compliance Resources */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 border border-orange-200">
                  <h3 className="text-2xl font-bold text-black mb-6">
                    <i className="ri-shield-check-line text-[#f59e0b] mr-3"></i>
                    Compliance Resources
                  </h3>
                  <div className="space-y-4">
                    <a
                      href="https://www.nj.gov/dca/divisions/codes/lead/inspectors.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-orange-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">Certified Inspectors</h4>
                          <p className="text-sm text-gray-600">Find EPA-certified lead inspectors in New Jersey</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                    <a
                      href="https://www.nj.gov/dca/divisions/codes/lead/training.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-orange-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">Training Programs</h4>
                          <p className="text-sm text-gray-600">Lead-safe training and certification programs</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                    <a
                      href="https://www.nj.gov/dca/divisions/codes/lead/faq.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-white rounded-lg p-4 hover:shadow-md transition-shadow duration-300 border border-orange-200 cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-black mb-1">Official FAQ</h4>
                          <p className="text-sm text-gray-600">Frequently asked questions from NJ DCA</p>
                        </div>
                        <i className="ri-external-link-line text-[#f59e0b] text-xl flex-shrink-0 ml-4"></i>
                      </div>
                    </a>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
                  <h3 className="text-2xl font-bold text-black mb-6">
                    <i className="ri-phone-line text-[#f59e0b] mr-3"></i>
                    Contact Information
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="font-semibold text-black mb-2">NJ DCA Lead-Safe Program</h4>
                      <p className="text-sm text-gray-600 mb-2">Phone: (609) 292-7899</p>
                      <p className="text-sm text-gray-600">Email: leadsafe@dca.nj.gov</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="font-semibold text-black mb-2">EPA Lead Hotline</h4>
                      <p className="text-sm text-gray-600 mb-2">Phone: 1-800-424-LEAD (5323)</p>
                      <p className="text-sm text-gray-600">Available 24/7 for lead-related questions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="mt-12 bg-yellow-50 border border-yellow-200 rounded-2xl p-8">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <i className="ri-information-line text-yellow-600 text-2xl"></i>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-black mb-2">Important Notice</h3>
                  <p className="text-gray-700 leading-relaxed">
                    This information is provided for general guidance only. Laws and regulations may change, and specific requirements may vary by municipality. Always consult with certified professionals and official government sources for the most current and accurate information regarding lead-safe compliance in New Jersey.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <section className="py-20 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-gradient-to-br from-[#f59e0b] to-[#d97706] rounded-3xl p-12 text-center shadow-2xl">
                <h2 className="text-4xl font-bold text-white mb-6">
                  Need a New Jersey Lead Inspection?
                </h2>
                <p className="text-xl text-white/95 mb-8 leading-relaxed">
                  Stay compliant with NJ's lead-safe housing laws. We'll handle the inspection, certification, and documentation so you can focus on your properties.
                </p>
                <Link
                  to="/contact#contact-form"
                  className="inline-block bg-white text-[#f59e0b] px-10 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
                >
                  Schedule Your Inspection
                </Link>
              </div>
            </div>
          </section>
        </div>
      </section>

      <Footer />
    </div>
  );
}
